import { currentLanguage } from './current'
import { LanguageCode, toSupportedLanguage } from './languages'

/**
 * Learner-language content from the API overlay (docs/plans/localization.md, "API overlay"), with
 * English as the fallback. Pure helpers: the components pick the text and its `lang` here, so
 * every surface falls back the same way.
 *
 * The API only adds the localized fields when the request language is a live learner language,
 * and each one is null when that item has no checked translation yet. English fields never change.
 */

export interface LocalizedText {
  /** What to show. */
  text: string
  /** The `lang` attribute for it: the learner language when localized, else 'en'. */
  lang: string
  /** True when `text` is the learner-language translation, not the English. */
  localized: boolean
}

const present = (value: unknown): value is string => typeof value === 'string' && value.trim() !== ''

/** A language the API localizes into: a supported code other than English, or null. */
export const learnerLanguage = (value: unknown): Exclude<LanguageCode, 'en'> | null => {
  const code = toSupportedLanguage(value)
  return code && code !== 'en' ? code : null
}

/** The localized text when there is one (with its language), else the English. */
export const pickLocalized = (localized: unknown, english: unknown, language: unknown): LocalizedText => {
  const lang = learnerLanguage(language)
  if (lang && present(localized)) return { text: localized, lang, localized: true }
  return { text: typeof english === 'string' ? english : '', lang: 'en', localized: false }
}

/** The English title of a story, card or history payload (camelCase, or Prismic rich text). */
export const englishStoryTitle = (story: any): string =>
  story?.englishTitle ?? story?.english_title?.[0]?.text ?? ''

/** `localizedTitle ?? englishTitle`. */
export const storyTitleText = (story: any): LocalizedText =>
  pickLocalized(story?.localizedTitle, englishStoryTitle(story), story?.contentLanguage)

/** `localizedIntroduction ?? storyIntroduction` (the introduction is English in the payload). */
export const storyIntroductionText = (story: any): LocalizedText =>
  pickLocalized(story?.localizedIntroduction, story?.storyIntroduction, story?.contentLanguage)

/** `localizedDefinition1/2 ?? definition1/2`, the teacher's notes on the two new words. */
export const storyDefinitionText = (story: any, n: 1 | 2): LocalizedText =>
  pickLocalized(
    n === 1 ? story?.localizedDefinition1 : story?.localizedDefinition2,
    n === 1 ? story?.definition1 : story?.definition2,
    story?.contentLanguage,
  )

/** `localizedContent[p] ?? englishContent[p]`, one paragraph of the story translation. */
export const storyParagraphText = (story: any, p: number): LocalizedText =>
  pickLocalized(story?.localizedContent?.[p], story?.englishContent?.[p], story?.contentLanguage)

/** Every paragraph of the story translation, localized where it can be. */
export const storyParagraphTexts = (story: any): LocalizedText[] =>
  Array.isArray(story?.englishContent)
    ? story.englishContent.map((_: unknown, p: number) => storyParagraphText(story, p))
    : []

/** `sentenceTranslations[i].tr ?? .en`. */
export const sentenceTranslationText = (
  entry: { tr?: string | null; en?: string | null } | null | undefined,
  contentLanguage: unknown,
): LocalizedText => pickLocalized(entry?.tr, (entry?.en || '').trim(), contentLanguage)

/**
 * Whether the story's translation (sentences or paragraphs) is served in a learner language, at
 * least in part. The title alone does not count: it is card metadata.
 */
export const storyHasLocalizedContent = (story: any): boolean => {
  if (!learnerLanguage(story?.contentLanguage)) return false
  const sentences = Array.isArray(story?.sentenceTranslations) ? story.sentenceTranslations : []
  const paragraphs = Array.isArray(story?.localizedContent) ? story.localizedContent : []
  return sentences.some((entry: any) => present(entry?.tr)) || paragraphs.some(present)
}

/**
 * The senses of a word gloss, most common first: the stored format is `; `-separated
 * (`manger; repas`). English glosses from the dictionary use the same separator.
 */
export const meaningSenses = (text: string | null | undefined): string[] =>
  (text || '')
    .split(/;\s*/)
    .map((sense) => sense.trim())
    .filter(Boolean)

export interface WordMeaning {
  /** The meaning to show first: the learner-language one when there is one, else English. */
  primary: LocalizedText
  /** The English meaning, shown under a localized one when the learner asked for it. */
  english: string
}

/**
 * A word's meaning: `localizedMeaning` (in `meaningLanguage`) when present, else the English.
 * `english` is the English gloss the payload carries (`english` on dictionary words, `meaning` on
 * pocket words).
 */
export const wordMeaning = (word: any, english?: string | null): WordMeaning => {
  const englishText = (english ?? word?.english ?? '') || ''
  return {
    primary: pickLocalized(word?.localizedMeaning, englishText, word?.meaningLanguage),
    english: englishText,
  }
}

/** An example sentence's translation: `tr` when present, else its English. */
export const exampleTranslationText = (example: any, language: unknown, english: unknown): LocalizedText =>
  pickLocalized(example?.tr, english, language)

/**
 * The `lang` to send with "Explain in context" (`get-word-context`): the interface language when
 * it is a learner language, else nothing (the answer is English).
 */
export const wordContextLanguage = (language: unknown = currentLanguage()): string | undefined =>
  learnerLanguage(language) || undefined
